/**
 * Hook de embed do PowerBI, sem wrapper de terceiro (spec 0009, decisao 2).
 *
 * Motivo: o que precisamos e `embed`, `reset`, o evento de erro e o relogio do token - poucas
 * dezenas de linhas. Um wrapper (`powerbi-client-react`) acrescentaria uma dependencia exatamente
 * na fronteira que costuma atrasar em major nova do React. O `powerbi-client` em si nao tem peer
 * dependency nenhuma: e JavaScript puro, indiferente a versao do React.
 *
 * Tambem tira da pagina um `<script>` de CDN carregado em runtime, que era como o app Streamlit
 * fazia.
 *
 * O que foi PRESERVADO do app antigo, porque custou diagnostico para descobrir:
 *   - `reset` antes de embutir (segunda montagem deixaria dois iframes no mesmo no);
 *   - vigia de 15s: embed que nao cria iframe costuma ser token ou permissao, e falha CALADO;
 *   - aviso um minuto antes de o token expirar, em vez de deixar a tela morrer sozinha.
 */

import { factories, models, service } from 'powerbi-client'
import { useEffect, useRef, useState } from 'react'

import type { EmbedToken } from '../api/types'

/** Uma instancia por aplicacao: o servico guarda os embeds vivos e nao deve ser recriado. */
const powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory)

const SEGUNDOS_ATE_DESCONFIAR = 15
const AVISO_ANTES_DE_EXPIRAR_MS = 60_000

export interface EstadoDoEmbed {
  erro: string | null
  expirando: boolean
}

export function usePowerbiEmbed(concessao: EmbedToken | null) {
  const container = useRef<HTMLDivElement | null>(null)
  const [estado, setEstado] = useState<EstadoDoEmbed>({ erro: null, expirando: false })

  useEffect(() => {
    const elemento = container.current
    if (elemento === null || concessao === null) return

    setEstado({ erro: null, expirando: false })

    // Sem isto, uma segunda montagem (StrictMode, troca de relatorio) deixa dois iframes no mesmo
    // no - e o de baixo continua consumindo o token antigo.
    powerbi.reset(elemento)

    let relatorio: ReturnType<typeof powerbi.embed> | null = null
    try {
      relatorio = powerbi.embed(elemento, {
        type: 'report',
        id: concessao.report_id,
        embedUrl: concessao.embed_url,
        accessToken: concessao.token,
        tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: true },
          },
          // Transparente: a margem do relatorio assume o fundo da nossa casca, em vez de um
          // retangulo branco dentro do tema escuro. O canvas do relatorio mantem as cores dele.
          background: models.BackgroundType.Transparent,
        },
      })
      relatorio.on('error', (evento: { detail?: unknown }) => {
        setEstado({ erro: descreverFalha(evento.detail ?? evento), expirando: false })
      })
    } catch (exc) {
      setEstado({ erro: descreverFalha(exc), expirando: false })
    }

    const vigia = window.setTimeout(() => {
      if (elemento.querySelector('iframe') === null) {
        setEstado((atual) => ({
          ...atual,
          erro:
            atual.erro ??
            `O Power BI nao criou o quadro do relatorio em ${SEGUNDOS_ATE_DESCONFIAR}s. ` +
              'Costuma ser token expirado ou permissao do service principal.',
        }))
      }
    }, SEGUNDOS_ATE_DESCONFIAR * 1000)

    const expiraEm =
      concessao.expires_at === null ? Number.NaN : Date.parse(concessao.expires_at) - Date.now() - AVISO_ANTES_DE_EXPIRAR_MS
    const relogio = Number.isFinite(expiraEm) && expiraEm > 0
      ? window.setTimeout(() => setEstado((atual) => ({ ...atual, expirando: true })), expiraEm)
      : null

    return () => {
      window.clearTimeout(vigia)
      if (relogio !== null) window.clearTimeout(relogio)
      powerbi.reset(elemento)
    }
  }, [concessao])

  return { container, ...estado }
}

/**
 * Erro do PowerBI em texto legivel.
 *
 * O objeto de erro dele nao serializa com `JSON.stringify` (as propriedades nao sao enumeraveis),
 * entao os campos conhecidos sao lidos um a um antes de qualquer tentativa generica.
 */
function descreverFalha(falha: unknown): string {
  if (falha === null || falha === undefined) return 'erro sem detalhe'
  if (typeof falha === 'string') return falha
  if (Array.isArray(falha)) return falha.map(descreverFalha).join('\n---\n')

  const objeto = falha as Record<string, unknown>
  const partes: string[] = []
  for (const campo of ['message', 'detailedMessage', 'errorCode', 'level', 'technicalDetails']) {
    const valor = objeto[campo]
    if (valor !== undefined && valor !== null && valor !== '') partes.push(`${campo}: ${String(valor)}`)
  }
  if (partes.length > 0) return partes.join('\n')

  try {
    const json = JSON.stringify(falha, Object.getOwnPropertyNames(falha))
    if (json !== undefined && json !== '{}') return json
  } catch {
    /* objeto ciclico ou exotico: cai no toString abaixo */
  }
  return Object.prototype.toString.call(falha)
}
